import React, { useEffect, useMemo, useState, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import {
  PencilIcon,
  Theme,
  Modal,
  ModalFooter,
  Button,
  TextArea,
  AlertService,
  TextInput,
  SelectOption,
} from '@spoiler-alert/ui-library';

import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import routePaths from '../../route-paths';
import { Breadcrumbs } from '../../store';
import { TitleService } from '../../services';
import { AdminDocumentTypeDataApprovalQuery, AdminSellerSiteDataApprovalQuery } from '../../graphql/queries';
import MemorySelect from '../../components/forms/memory-select';
import { AdminSaveDocumentApprovalMutation } from '../../graphql/mutations';
import { VTable, VColumn, VRowAction } from '../../components/v-table';

const useStyles = createUseStyles({
  gridWrap: {
    width: '100%',
    position: 'relative',
    border: `1px ${Theme.grey30} solid`,
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  inputElement: {
    width: '100%',
    marginRight: 8,
    display: 'flex',
    justifyContent: 'space-between',
  },
  searchElement: {
    width: 200,
    marginLeft: 16,
    flexShrink: 0,
  },
  siteSelectContainer: {
    minWidth: 350,
    maxWidth: 500,
  },
  documentTypeSelectContainer: {
    minWidth: 300,
    maxWidth: 400,
  },
  modalBody: {
    padding: 24,
    width: '100%',
  },
  subTitle: {
    textAlign: 'left',
    fontSize: 16,
    margin: '4px 0px 16px 0px',
  },
  categoryLabel: {
    color: Theme.red,
    fontWeight: 'bold',
  },
  modalTitle: {
    fontSize: 22,
    marginBottom: 0,
  },
  modalSubTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 4,
    marginTop: 16,
  },
  customTextArea: {
    marginTop: 16,
    width: '100%',
  },
});

const ApprovalManager = () => {
  const classes = useStyles();
  const [showModal, setShowModal] = useState(false);
  const [customDescription, setCustomDescription] = useState('');
  const [siteSelection, setSiteSelection] = useState(null);
  const [documentTypeSelection, setDocumentTypeSelection] = useState(null);
  const [currentRow, setRow] = useState(null);
  const [searchText, setSearchText] = useState('');
  const visibleRowsRef = useRef([]);
  const {
    data: siteList,
    loading: siteListLoading,
    error: siteListError,
    refetch: refetchSites,
  } = useQuery(AdminSellerSiteDataApprovalQuery);
  const [queryDocuments, { data: documentList, refetch: refetchDocuments }] = useLazyQuery(
    AdminDocumentTypeDataApprovalQuery,
  );
  const [saveDocument, { loading: SaveDocumentLoading }] = useMutation(AdminSaveDocumentApprovalMutation);
  const sites = siteList?.adminSellerSiteDataApprovalQuery || [];
  const documentData = documentList?.adminDataApprovalQuery || [];

  const columns = [
    new VColumn({
      field: '_id',
      displayName: 'ID',
      visible: true,
      sortable: true,
    }),
    new VColumn({
      field: 'itemName',
      displayName: 'Item Name',
      visible: true,
      sortable: true,
    }),
    new VColumn({
      field: 'itemDesc',
      displayName: 'Description',
      visible: true,
      sortable: true,
      defaultSort: true,
      defaultSortDirection: 'desc',
    }),
    new VColumn({
      field: 'foodType',
      displayName: 'Food Type',
      visible: true,
      sortable: true,
    }),
    new VColumn({
      field: 'foodSubtype',
      displayName: 'Food Subtype',
      visible: true,
      sortable: true,
    }),
    new VColumn({
      field: 'brand',
      displayName: 'Brand',
      visible: true,
      sortable: true,
    }),
    new VColumn({
      field: 'unitUPC',
      displayName: 'UPC',
      visible: true,
      sortable: true,
    }),
    new VColumn({
      field: 'grossWeightPerUOM',
      displayName: 'Weight (per UOM)',
      visible: true,
      sortable: true,
    }),
    new VColumn({
      field: 'packQty',
      displayName: 'Pack Qty',
      visible: true,
      sortable: true,
    }),
    new VColumn({
      field: 'enhancedDescription',
      displayName: 'Enhanced Description',
      visible: true,
      sortable: true,
      style: { color: Theme.red, fontWeight: 'bold' },
      width: 700,
    }),
    new VColumn({
      field: 'enhancedDescriptionOrigin',
      displayName: 'Origin',
      visible: true,
      sortable: true,
    }),
    new VColumn({
      field: 'enhancedDescriptionStatus',
      displayName: 'Enhanced Description Status',
      visible: true,
      sortable: true,
      width: 180,
    }),
  ];

  useEffect(() => {
    TitleService.setTitles('Data Approval Manager');
    Breadcrumbs.set([
      {
        url: routePaths.approvalManager,
        title: 'Data Approval Manager',
      },
    ]);
  }, []);

  useEffect(() => {
    if (siteSelection?.[0]?.value && documentTypeSelection?.[0]?.value) {
      queryDocuments({
        variables: { siteName: siteSelection[0].value, documentType: documentTypeSelection[0].value },
      });
    }
  }, [siteSelection, documentTypeSelection, queryDocuments]);

  const documentTypeList = useMemo(() => {
    if (siteSelection?.length) {
      const foundSite = siteList?.adminSellerSiteDataApprovalQuery?.find((site) => site._id === siteSelection[0].value);
      const docs = [];
      docs.push({ _id: 'item', name: `Items (${foundSite?.needsApproval || 0} need approval)` });
      // docs.push({ _id: 'item', name: 'Item' });
      // docs.push({ _id: 'offerListings', name: 'Offer Listings' });
      return docs;
    }
    return [];
  }, [siteSelection, siteList]);

  const adjustDescription = (row) => {
    setRow(row);
    setCustomDescription(row.enhancedDescription || '');
    setShowModal(true);
  };

  const hideModal = () => {
    setShowModal(false);
    setRow(null);
    setCustomDescription('');
  };

  const saveEditedDescription = async (enhancedDescription, itemId) => {
    try {
      await saveDocument({
        variables: {
          enhancedDescription,
          documentIds: [itemId],
          documentType: documentTypeSelection[0].value,
          siteName: siteSelection[0].value,
        },
      });
      AlertService.alert({
        type: 'success',
        message: 'Enhanced description saved successfully',
        autoDismiss: true,
        dismissDelay: 3000,
      });
      hideModal();
      await Promise.all([refetchDocuments(), refetchSites()]);
    } catch (error) {
      AlertService.alert({
        type: 'warning',
        message: `Error saving enhanced description: ${error.message}`,
        autoDismiss: true,
        dismissDelay: 5000,
      });
    }
  };

  const adjustModal = () => {
    return (
      <Modal onHide={hideModal} open={showModal} closeOnEsc closeOnOutsideClick>
        <div className={classes.modalBody}>
          <h2 className={classes.modalTitle}>Edit Enhanced Description</h2>
          <div className={classes.subTitle}>
            <label>Edit the enhanced description for the selected item.</label>
            <div>
              <p className={classes.modalSubTitle}>Current Description</p>
              <div>
                <label className={classes.categoryLabel}>{currentRow?.itemDesc}</label>
              </div>
            </div>
            <div>
              <p className={classes.modalSubTitle}>AI-Generated Enhanced Description</p>
              <div>
                <label className={classes.categoryLabel}>{currentRow?.enhancedDescription}</label>
              </div>
            </div>
          </div>
          <div>
            <p className={classes.modalSubTitle}>Edit Enhanced Description</p>
            <TextArea
              placeholder="Enter your edited enhanced description..."
              value={customDescription}
              onChange={setCustomDescription}
              rows={4}
            />
          </div>
        </div>
        <ModalFooter>
          <Button type="button" onClick={hideModal} link>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!customDescription.trim()}
            primary
            loading={SaveDocumentLoading}
            loadingText="Saving..."
            onClick={() => {
              saveEditedDescription(customDescription.trim(), currentRow._id);
            }}
          >
            Save Changes
          </Button>
        </ModalFooter>
      </Modal>
    );
  };

  return (
    <div>
      {siteListError && (
        <div style={{ color: 'red', marginBottom: '16px' }}>Error loading sites: {siteListError.message}</div>
      )}
      {sites && sites.length === 0 && !siteListLoading && (
        <div style={{ color: 'orange', marginBottom: '16px' }}>No sites found with items needing approval.</div>
      )}
      <div className={classes.controls}>
        <div className={classes.siteSelectContainer}>
          <MemorySelect
            cacheId="ManagerDataApprovalSiteSelect"
            search
            placeholderText={siteListLoading ? 'Loading sites...' : 'Select a Site'}
            onChange={(items) => setSiteSelection(items)}
            selectedItems={siteSelection}
            loading={siteListLoading}
          >
            {Array.isArray(sites) &&
              sites
                .filter((s) => s && s._id)
                .map((s) => (
                  <SelectOption key={s._id} value={s._id} searchText={s._id}>
                    {s._id} ({s.needsApproval || 0} need approval)
                  </SelectOption>
                ))}
          </MemorySelect>
        </div>
        {siteSelection && siteSelection.length > 0 && (
          <div className={classes.documentTypeSelectContainer}>
            <MemorySelect
              cacheId="DataApprovalDocumentSelect"
              search
              placeholderText="Select a Document Type"
              onChange={(items) => setDocumentTypeSelection(items)}
              selectedItems={documentTypeSelection}
            >
              {documentTypeList.map((s) => (
                <SelectOption key={s._id} value={s._id} searchText={s.name}>
                  {s.name}
                </SelectOption>
              ))}
            </MemorySelect>
          </div>
        )}
        {siteSelection && documentTypeSelection && (
          <TextInput
            value={searchText}
            labelText="Search"
            onChange={(value) => setSearchText(value)}
            className={classes.searchElement}
          />
        )}
      </div>

      <div className={classes.gridWrap}>
        {siteSelection && documentTypeSelection && (
          <VTable
            key={`${siteSelection[0]?.value}-${documentTypeSelection[0]?.value}`}
            searchText={searchText}
            data={documentData}
            loading={false}
            noDataMessage="No data available"
            border
            sticky
            stickyOffset={0}
            cols={columns}
            visibleRowsRef={visibleRowsRef}
            rowActions={[
              <VRowAction
                key="edit-description"
                tooltipText="Edit enhanced description"
                icon={PencilIcon}
                onClick={(row) => () => adjustDescription(row)}
              />,
            ]}
          />
        )}
        {adjustModal()}
      </div>
    </div>
  );
};

export default ApprovalManager;
